import {useCallback, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import MapService from '../services/map.service'
import {hideModal} from '../store_redux/reducer/modal.reducer'
import {RootState} from '../store_redux'
import {ShippingAddressDetails} from '../props_reusable/visit'
import RevGeoCodeRes from '../props_reusable/map'
import {PopupState} from '../props_reusable'

interface CoordinateRef {
  latitude: number
  longitude: number
}

const useEditLocation = () => {
  const rootState = useSelector((state: RootState) => state)
  const dispatch = useDispatch()
  const {labels, settings, modal} = rootState
  const {latitude = 0, longitude = 0} = modal.data.location.coordinate || {}
  const coordinateRef = useRef<CoordinateRef>({latitude, longitude})
  const [address, setAddress] = useState(
    modal.data.location.addressDetails || ({} as ShippingAddressDetails),
  )
  const [loading, setLoading] = useState(false)
  const [popup, setPopup] = useState<PopupState>({open: false, type: null})
  const {contactName} = address

  const closeModal = () => {
    dispatch(hideModal())
  }

  const close = () => {
    if (
      coordinateRef.current.latitude !== latitude ||
      coordinateRef.current.longitude !== longitude
    ) {
      setPopup({open: true, type: 'cancel_popup'})
    } else {
      closeModal()
    }
  }

  const closePopup = () => {
    setPopup({open: false, type: null})
  }

  const okHandler = () => {
    if (address.postcode !== modal.data.location.addressDetails?.postcode) {
      setPopup({
        open: true,
        type: 'location_too_far_popup',
      })
    } else {
      closeModal()
    }
  }

  const setAddressBasedOnGeoCode = useCallback(
    (lat: number, lng: number): Promise<unknown> => {
      setLoading(true)
      return MapService.getAddress<RevGeoCodeRes>(
        `${lat},${lng}`,
        settings.mapApiKey,
      )
        .then(data => {
          const addressDetails = data.items[0].address
          coordinateRef.current = {
            latitude: lat,
            longitude: lng,
          }
          setAddress({
            contactName,
            addressLine1: addressDetails.houseNumber ?? '',
            addressLine2: addressDetails.street,
            addressLine3: addressDetails.city,
            addressLine4: '',
            countryName: addressDetails.countryName,
            countryDesc: addressDetails.countryCode,
            postcode: addressDetails.postalCode,
          })
        })
        .catch(err => {
          coordinateRef.current = {
            latitude,
            longitude,
          }
          setAddress(
            modal.data.location.addressDetails ||
              ({} as ShippingAddressDetails),
          )
          throw new Error(err.message)
        })
        .finally(() => setLoading(false))
    },
    [
      settings.mapApiKey,
      contactName,
      modal.data.location.addressDetails,
      latitude,
      longitude,
    ],
  )

  return {
    loading,
    labels,
    address,
    latitude,
    longitude,
    settings,
    setAddressBasedOnGeoCode,
    close,
    popup,
    closePopup,
    closeModal,
    okHandler,
  }
}

export default useEditLocation
